import { Fragment, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import UserForm from "./UserForm";
import { useRoles } from "../../../hooks/useRoles";
import { getUserById, updateUser } from "../../../services/userMgmtApi";
import { fetchDepartments } from "../../../services/departmentApi";

const EMPLOYEE_ROLE_ID = process.env.REACT_APP_ROLE_EMPLOYEE_ID;

/** Extract a plain string ID from a string or populated object. */
const extractId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  return String(val._id ?? val.id ?? "");
};

/** Unwrap the c_success / c_results envelope the backend sends. */
const unwrapUser = (raw) =>
  raw?.results?.data ?? raw?.data?.data ?? raw?.data ?? raw;

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Roles dropdown
  const { roles, loading: rolesLoading, error: rolesError } = useRoles();

  // Departments dropdown
  const [departments, setDepartments] = useState([]);
  const [depsLoading, setDepsLoading] = useState(true);
  const [depsError,   setDepsError]   = useState(null);

  // User data for form
  const [initialValues,  setInitialValues]  = useState(null);
  const [fetchLoading,   setFetchLoading]   = useState(true);
  const [fetchError,     setFetchError]     = useState(null);

  // Load departments once
  useEffect(() => {
    fetchDepartments()
      .then(setDepartments)
      .catch((err) => setDepsError(err.message || "Failed to load departments"))
      .finally(() => setDepsLoading(false));
  }, []);

  // Load user — wait until roles are ready so roleId can be matched immediately
  useEffect(() => {
    if (rolesLoading) return; // wait for roles first to avoid flicker

    setFetchLoading(true);
    getUserById(id)
      .then((raw) => {
        const data = unwrapUser(raw);

        const roleId     = extractId(data.roleId);
        const deptId     = extractId(data.departmentId);

        setInitialValues({
          userName:     data.userName     || "",
          email:        data.email        || "",
          password:     "",
          mobileNumber: data.mobileNumber || "",
          designation:  data.designation  || "",
          roleId,
          departmentId: deptId,
        });
      })
      .catch((err) => setFetchError(err.message || "Failed to load user"))
      .finally(() => setFetchLoading(false));
  }, [id, rolesLoading]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        userName:     values.userName,
        email:        values.email,
        mobileNumber: values.mobileNumber,
        designation:  values.designation,
        roleId:       values.roleId,
        departmentId: values.roleId === EMPLOYEE_ROLE_ID ? (values.departmentId || null) : null,
      };
      if (values.password) payload.password = values.password;

      await updateUser(id, payload);

      Swal.fire({
        icon:               "success",
        title:              "User Updated",
        text:               "User has been updated successfully.",
        confirmButtonColor: "#6418c3",
      }).then(() => navigate("/user-list"));
    } catch (err) {
      Swal.fire({
        icon:               "error",
        title:              "Error",
        text:               err.message || "Something went wrong. Please try again.",
        confirmButtonColor: "#6418c3",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (fetchLoading || rolesLoading) {
    return (
      <Fragment>
        <PageTitle activeMenu="Edit User" motherMenu="Users" pageContent="Edit User" />
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading user details...</p>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  if (fetchError) {
    return (
      <Fragment>
        <PageTitle activeMenu="Edit User" motherMenu="Users" pageContent="Edit User" />
        <div className="row">
          <div className="col-xl-12">
            <div className="alert alert-danger">
              Failed to load user: {fetchError}
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageTitle activeMenu="Edit User" motherMenu="Users" pageContent="Edit User" />
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Edit User</h4>
            </div>
            <div className="card-body">
              {rolesError && (
                <div className="alert alert-warning mb-3">
                  Could not load roles: {rolesError}
                </div>
              )}
              <UserForm
                initialValues={initialValues}
                roles={roles}
                rolesLoading={rolesLoading}
                rolesError={rolesError}
                departments={departments}
                depsLoading={depsLoading}
                depsError={depsError}
                onSubmit={handleSubmit}
                isEditMode={true}
              />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditUser;